import {
  getBusinessWifiPriceSelections,
  getBusinessWifiTotals
} from './businessWifi/businessWifi';

import {
  getCustomerInformationTotals,
  getCustomerInformationPriceSelections
} from './customerInformation/customerInformations';

import {
  getFibreInternetTotals,
  getFibreInternetPriceSelections
} from './fibeInternet/fibreInternet';

import {
  getMobilityTotals,
  getMobilityPriceSelections
} from './mobility/mobility';

import summaryConfig, { GET_FIELD_VALUE } from './utils/summaryConfig';
import getFieldSummaryDetailsForOneOption, {
  getFieldSummaryDetailsFromField,
  getSummaryDetailsForFieldWithSelectedOptions
} from './utils/getFieldSummaryDetails';
import {
  getSecureBusinessTotals,
  getSecureBusinessPriceSelections
} from './secureBusiness/secureBusiness';

import removePriceSubstring from './utils/removePriceSubstring';

const vulcanPricing = (formFields, orderData) => {
  const { priceSelections = {} } = orderData;
  const totalResults = [
    getCustomerInformationTotals(formFields, priceSelections),
    getFibreInternetTotals(formFields, priceSelections),
    getBusinessWifiTotals(formFields, priceSelections),
    getMobilityTotals(formFields, priceSelections),
    getSecureBusinessTotals(formFields, priceSelections)
  ];
  const total = {
    monthly: 0,
    oneTime: 0,
    threeYearEasyPayment: 0,
    fiveYearEasyPayment: 0
  };
  totalResults.forEach(result => {
    total.oneTime += result.oneTime;
    total.monthly +=
      result.monthly + result.threeYearEasyPayment + result.fiveYearEasyPayment;
  });
  return total;
};

export const getVulcanPriceSelections = formFields => {
  let selections = {};
  const priceSelections = [
    getCustomerInformationPriceSelections(formFields),
    getFibreInternetPriceSelections(formFields),
    getBusinessWifiPriceSelections(formFields),
    getMobilityPriceSelections(formFields),
    getSecureBusinessPriceSelections(formFields)
  ];
  priceSelections.forEach(selection => {
    selections = { ...selections, ...selection };
  });
  return selections;
};

export const getPricingSummary = formFields => {
  if (!formFields) return [];
  return summaryConfig
    .filter(summaryConfigSectionItem => {
      return (
        summaryConfigSectionItem.shouldShowSection &&
        summaryConfigSectionItem.shouldShowSection(formFields)
      );
    })
    .map(summaryConfigSectionItem => {
      const total = {
        monthlyCharges: 0,
        oneTimeCharges: 0,
        threeYearEasyPaymentCharges: 0,
        fiveYearEasyPaymentCharges: 0
      };
      return {
        sectionTitle: summaryConfigSectionItem.sectionTitle,
        rows: summaryConfigSectionItem.rows.map(row => {
          let rowDetails = { qty: 0, monthlyCharges: 0, oneTimeCharges: 0 };
          if (row.checkForOption) {
            rowDetails = getFieldSummaryDetailsForOneOption(
              formFields[row.checkForKey],
              row.checkForOption
            );
            const {
              qty,
              monthlyCharges,
              oneTimeCharges,
              threeYearEasyPaymentCharges,
              fiveYearEasyPaymentCharges
            } = rowDetails;
            total.monthlyCharges += monthlyCharges;
            total.oneTimeCharges += oneTimeCharges;
            total.threeYearEasyPaymentCharges += threeYearEasyPaymentCharges;
            total.fiveYearEasyPaymentCharges += fiveYearEasyPaymentCharges;
            return {
              label: row.label,
              description: row.description,
              qty,
              monthlyCharges,
              oneTimeCharges,
              threeYearEasyPaymentCharges,
              fiveYearEasyPaymentCharges
            };
          }
          if (row.checkForKey) {
            rowDetails = getFieldSummaryDetailsFromField(
              formFields[row.checkForKey]
            );
            const {
              qty,
              description,
              monthlyCharges,
              oneTimeCharges,
              threeYearEasyPaymentCharges,
              fiveYearEasyPaymentCharges
            } = rowDetails;
            let returnObj = {
              label: row.label,
              description:
                row.description === GET_FIELD_VALUE
                  ? formFields[row.checkForKey].value
                  : row.description || description,
              qty,
              monthlyCharges,
              oneTimeCharges,
              threeYearEasyPaymentCharges,
              fiveYearEasyPaymentCharges
            };
            if (row.transformer) {
              returnObj = row.transformer(returnObj);
            }
            total.monthlyCharges += returnObj.monthlyCharges;
            total.oneTimeCharges += returnObj.oneTimeCharges;
            total.threeYearEasyPaymentCharges +=
              returnObj.threeYearEasyPaymentCharges;
            total.fiveYearEasyPaymentCharges +=
              returnObj.fiveYearEasyPaymentCharges;
            return returnObj;
          }
          if (row.listSelectedOptionsForKey) {
            getSummaryDetailsForFieldWithSelectedOptions(
              formFields[row.listSelectedOptionsForKey]
            ).forEach(summaryItem => {
              total.monthlyCharges += summaryItem.monthlyCharges;
              total.oneTimeCharges += summaryItem.oneTimeCharges;
              total.threeYearEasyPaymentCharges +=
                summaryItem.threeYearEasyPaymentCharges;
              total.fiveYearEasyPaymentCharges +=
                summaryItem.fiveYearEasyPaymentCharges;
            });
            return {
              label: row.label,
              rows: getSummaryDetailsForFieldWithSelectedOptions(
                formFields[row.listSelectedOptionsForKey]
              )
            };
          }
          if (
            row.listSelectedValuesForEquipment &&
            formFields[row.listSelectedValuesForEquipment.basedOnKey].value
          ) {
            rowDetails = getFieldSummaryDetailsFromField(
              formFields[row.checkForKey]
            );
            const selectedBaseValue =
              formFields[row.listSelectedValuesForEquipment.basedOnKey].value;
            const requiredEquipmentKeys =
              row.listSelectedValuesForEquipment.map[selectedBaseValue];
            const rows = [];
            requiredEquipmentKeys.forEach(key => {
              Object.keys(formFields[key]).forEach(equipmentItem => {
                let shouldCalculate = true;
                const filterKeyBySubstringValue =
                  row.listSelectedValuesForEquipment.filterKeysBySubstring;
                const filterKeyBySubstringNegativeValues =
                  row.listSelectedValuesForEquipment
                    .filterKeysBySubstringNegative;
                if (filterKeyBySubstringValue) {
                  shouldCalculate = equipmentItem.includes(
                    filterKeyBySubstringValue
                  );
                }
                if (filterKeyBySubstringNegativeValues) {
                  shouldCalculate = !filterKeyBySubstringNegativeValues.some(
                    substring => {
                      return equipmentItem.includes(substring);
                    }
                  );
                }
                if (shouldCalculate) {
                  rowDetails = getFieldSummaryDetailsFromField(
                    formFields[key][equipmentItem]
                  );
                  if (+rowDetails.qty)
                    rows.push({
                      ...rowDetails,
                      qty: +rowDetails.qty,
                      description: removePriceSubstring(
                        formFields[key][equipmentItem].fieldLabel || ''
                      ),
                      monthlyCharges:
                        rowDetails.monthlyCharges ||
                        rowDetails.threeYearEasyPaymentCharges ||
                        rowDetails.fiveYearEasyPaymentCharges
                    });
                  total.monthlyCharges +=
                    rowDetails.monthlyCharges +
                    rowDetails.threeYearEasyPaymentCharges +
                    rowDetails.fiveYearEasyPaymentCharges;
                  total.oneTimeCharges += rowDetails.oneTimeCharges;
                  total.threeYearEasyPaymentCharges +=
                    rowDetails.threeYearEasyPaymentCharges;
                  total.fiveYearEasyPaymentCharges +=
                    rowDetails.fiveYearEasyPaymentCharges;
                }
              });
            });
            return {
              label: row.label,
              rows
            };
          }
          return {};
        }),
        total
      };
    });
};

export default vulcanPricing;
